import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  // ObservedElements class is the class name for all elements that the menu should observe. It can (and usually will) be outside the scope of the controller
  static values = {
    observedElementsClass: String,
    menuItemClass: String,
  };
  static targets = ['navigationMenu', 'menuItem'];

  connect() {
    this.allowScrollUpdate = true;
    this.#navigationMenuTracking();
  }

  // When the menu itself scrolls, it will stop the main content from scrolling, so it needs to be paused and resumed once the page has finished scrolling
  pauseMenuScrolling() {
    this.allowScrollUpdate = false;
    document.onscrollend = () => {
      this.allowScrollUpdate = true;
    };
  }

  // When the user scrolls down the page, the menu items currently visible should be highlighted, and if the sidebar menu overflows, we should scroll to ensure the currently visible elements are visible
  #navigationMenuTracking() {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        const menuItem = this.#getMenuItem(entry.target.id);

        if (entry.isIntersecting) {
          if (menuItem) menuItem.classList.add('active-item');

          if (this.allowScrollUpdate) {
            this.#scrollMenuToPosition(this.#getMenuScrollPosition(menuItem));
          }
        } else {
          if (menuItem) menuItem.classList.remove('active-item');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, { threshold: 0.75 });
    document.querySelectorAll('.' + this.observedElementsClassValue).forEach((card) => observer.observe(card));
  }

  // Scroll the  menu to keep the active items visible
  #scrollMenuToPosition(position) {
    this.navigationMenuTarget.scrollTo({
      top: position,
      behavior: 'smooth',
    });
  }

  // Get the current menu item via its link from the element on the page it is observing
  #getMenuItem(targetId) {
    const navItemSelector = `a[href="#${targetId}"]`;
    const navItem = this.navigationMenuTarget.querySelector(navItemSelector);
    return navItem.querySelector('.' + this.menuItemClassValue);
  }

  // Get the offset in pixels that the menu item is relative to the top of its container
  #getMenuScrollPosition(menuItem) {
    const itemTop = menuItem.offsetTop;
    const itemHeight = menuItem.offsetHeight;
    const containerHeight = this.navigationMenuTarget.offsetHeight;
    return itemTop + itemHeight / 2 - containerHeight / 2;
  }
}
